import { listOfflineSales, updateOfflineSale } from './db';
import type { OfflineSaleRecord } from './types';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const errorMessage = async (response: Response) => {
  try {
    const value = await response.json();
    return value?.message || JSON.stringify(value);
  } catch {
    return `${response.status} ${response.statusText}`;
  }
};

const pollOperation = async (key: string) => {
  for (let i = 0; i < 24; i += 1) {
    const response = await fetch(`/api/operations/${encodeURIComponent(key)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      const operation = await response.json();
      if (operation?.status === 'done') return operation.response;
      if (operation?.status === 'failed')
        throw new Error(operation?.error?.message || 'La operación offline fue rechazada.');
    } else if (response.status !== 404) {
      throw new Error(await errorMessage(response));
    }
    await sleep(Math.min(1000 + i * 250, 3000));
  }
  throw new Error('No se pudo confirmar la venta todavía. Se mantendrá pendiente para reintento.');
};

const syncOne = async (sale: OfflineSaleRecord) => {
  await updateOfflineSale(sale.id, {
    status: 'syncing',
    attempts: sale.attempts + 1,
    lastError: undefined,
  });
  try {
    const response = await fetch('/api/salemovements', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': sale.idempotencyKey,
        'X-ePos-Offline-Sale-Id': sale.id,
        'X-ePos-Offline-Correlative': String(sale.invoiceCorrelative),
      },
      body: JSON.stringify({
        ...sale.payload,
        offlineOperationId: sale.id,
        offlineReceiptCode: sale.receiptCode,
        invoiceCorrelative: sale.invoiceCorrelative,
        offlineCreatedAt: sale.createdAt,
      }),
    });

    if (response.ok) {
      const body = await response.json();
      const result =
        body?.status === 'processing' ? await pollOperation(sale.idempotencyKey) : body;
      await updateOfflineSale(sale.id, {
        status: 'synced',
        serverSaleId: result?.id,
        lastError: undefined,
      });
      return;
    }

    if (response.status === 409) {
      // A diferencia del Angular legado, 409 NO significa automáticamente "sincronizado".
      try {
        const result = await pollOperation(sale.idempotencyKey);
        await updateOfflineSale(sale.id, {
          status: 'synced',
          serverSaleId: result?.id,
          lastError: undefined,
        });
      } catch (error) {
        await updateOfflineSale(sale.id, {
          status: 'conflict',
          lastError:
            error instanceof Error ? error.message : 'Conflicto de idempotencia/correlativo.',
        });
      }
      return;
    }

    await updateOfflineSale(sale.id, { status: 'failed', lastError: await errorMessage(response) });
  } catch (error) {
    await updateOfflineSale(sale.id, {
      status: 'failed',
      lastError: error instanceof Error ? error.message : String(error),
    });
  }
};

export const syncOfflineSales = async (scope?: {
  companyTaxId: string;
  pointOfSaleId: string;
}): Promise<void> => {
  const pending = (await listOfflineSales(['pending', 'failed', 'conflict'])).filter(
    (sale) =>
      !scope ||
      (sale.companyTaxId === scope.companyTaxId && sale.pointOfSaleId === scope.pointOfSaleId)
  );
  for (const sale of pending) await syncOne(sale);
};
